import { z } from "zod";
import { directionToFaceUv, faceUvToDirection, sToU, uToS, type Vector3 } from "./cube";
import { type BasemapFeature, FACE_UNITS } from "./features";
import {
  BASEMAP_COARSEST_LEVEL,
  BASEMAP_FORMAT,
  BASEMAP_TILE_PIXELS,
  type BasemapFeatureSetRules,
  type BasemapGeometry,
} from "./manifest";
import { type PmtilesTile, writePmtiles } from "./pmtiles";
import { pmtilesAddress, type TilePlace, tilesAcross, transform } from "./tiles";
import { childrenOf, encodeDetailMap, LeafTree } from "./tree";

/**
 * A feature set's published tiles: every feature clipped into pieces, so a renderer streams small,
 * self-contained tiles. Each tile holds a piece of every feature whose footprint touches it,
 * clipped to the tile plus a margin, with each vertex's place in the feature and each edge clipping
 * made marked. At a place's finest level a tile is exact; a place with too many vertices for one
 * tile goes a level deeper, as the set's detail map records. Coarser tiles are simplified to their
 * pixel size, dropping what's too small to see.
 *
 * Clipping happens on the sphere itself: a tile's sides are great circles, so each is a plane
 * through the world's middle, and an edge, a great-circle arc, crosses it exactly where its chord
 * does. A plane's tiles are clipped the same way, with straight edges.
 */

/** A tile holds at most this many vertices; a place with more at its finest goes a level deeper. */
export const FEATURE_TILE_MOST_VERTICES = 16_384;
/** The deepest level a feature set's detail goes, however crowded a place is. */
const FEATURE_DEEPEST_LEVEL = 24;
/** The margin round each tile, in its pixels, so strokes and fills meet across tile edges. */
const MARGIN_PIXELS = 4;

/** One piece of a feature in a tile: part of one of its lines, or one ring of one of its polygons. */
const FeaturePiece = z.strictObject({
  part: z.int().min(0).meta({ description: "Which of the feature's lines or polygons it's from." }),
  ring: z.int().min(0).optional().meta({
    description: "A polygon piece's ring: 0 for the outer ring, then its holes.",
  }),
  positions: z
    .array(
      z
        .int()
        .min(0)
        .max(FACE_UNITS - 1),
    )
    .meta({
      description:
        "Across and down the tile's face for each vertex in turn, as whole numbers to 2^32.",
    }),
  vertices: z.array(z.int().min(-1)).meta({
    description:
      "Each vertex's place in its line or ring of the whole feature, or -1 for a vertex clipping made.",
  }),
  widths: z.array(z.number().min(0)).optional().meta({
    description: "A line piece's width at each vertex, in meters.",
  }),
  clippedEdges: z.array(z.int().min(0)).optional().meta({
    description:
      "A ring piece's edges along the tile's clipped edge, by the vertex each starts at, which renderers never stroke. A line piece's clipped ends are its -1 vertices.",
  }),
});

export const BasemapFeatureTile = z
  .strictObject({
    features: z.array(z.strictObject({ id: z.uuid(), pieces: z.array(FeaturePiece).min(1) })),
    properties: z.record(z.string(), z.array(z.union([z.string(), z.number(), z.boolean()]))).meta({
      description: "One column per property the set declares, a value per feature in order.",
    }),
  })
  .meta({
    id: "BasemapFeatureTile",
    description: "One tile of a feature set: gzipped JSON in a PMTiles file of tile type 0.",
  });
export type BasemapFeatureTile = z.infer<typeof BasemapFeatureTile>;

/** Reads one feature tile's bytes, as its PMTiles file stores them: gzipped JSON. */
export async function decodeFeatureTile(bytes: Uint8Array): Promise<BasemapFeatureTile> {
  const json = await transform(bytes, new DecompressionStream("gzip"));
  return BasemapFeatureTile.parse(JSON.parse(new TextDecoder().decode(json)));
}

/** A vertex while it's clipped: where it is, its place in the feature, and its line's width. */
interface Vertex {
  p: Vector3;
  index: number;
  width: number;
  /** Whether the edge from it to the next runs along a clipped side. */
  clipped: boolean;
}

/** One line or ring of one feature, as tiles cut it. */
interface Chain {
  feature: number;
  part: number;
  ring?: number;
  closed: boolean;
  vertices: Vertex[];
}

/** How a world's face positions become points that tile sides can cut, and back. */
interface Surface {
  /** A face's plane coordinate, u, for a position across it from 0 to 1. */
  across: (share: number) => number;
  back: (u: number) => number;
  /** A tile's margin, as a share of its face, to fit a line this many meters wide. */
  reach: (meters: number) => number;
}

const surfaceOf = (geometry: BasemapGeometry): Surface =>
  geometry.kind === "sphere"
    ? {
        across: share => sToU(share),
        back: uToS,
        // Near a face's middle a share of the face spans a quarter turn.
        reach: meters => (meters / 2 / geometry.radiusMeters) * (2 / Math.PI) * 1.5,
      }
    : {
        across: share => 2 * share - 1,
        back: u => (u + 1) / 2,
        reach: meters => meters / 2 / geometry.widthMeters,
      };

/** A face's middle, and its across and down directions, from the cube's face layout. */
function faceAxes(face: number): { N: Vector3; U: Vector3; V: Vector3 } {
  const N = faceUvToDirection(face, 0, 0);
  const atU = faceUvToDirection(face, 1, 0);
  const atV = faceUvToDirection(face, 0, 1);
  return {
    N,
    U: [atU[0] - N[0], atU[1] - N[1], atU[2] - N[2]],
    V: [atV[0] - N[0], atV[1] - N[1], atV[2] - N[2]],
  };
}

const dot = (a: Vector3, b: Vector3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const lerp = (a: Vector3, b: Vector3, t: number): Vector3 => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];

/**
 * The sides of a place plus `margin` (a share of its face), each a plane whose positive side is
 * inside, kept to its face. The first keeps the face's own half of the world.
 */
function sidesOf(surface: Surface, place: TilePlace, margin: number): Vector3[] {
  const { N, U, V } = faceAxes(place.face);
  const across = tilesAcross(place.level);
  const clamp = (share: number) => Math.min(1, Math.max(0, share));
  const s0 = surface.across(clamp(place.x / across - margin));
  const s1 = surface.across(clamp((place.x + 1) / across + margin));
  // Rows run down the face, and t runs up it.
  const t0 = surface.across(clamp(1 - (place.y + 1) / across - margin));
  const t1 = surface.across(clamp(1 - place.y / across + margin));
  // Inside is s0 ≤ u ≤ s1 and t0 ≤ v ≤ t1, where u = p·U / p·N: each a plane through the middle.
  const side = (onN: number, axis: Vector3, sign: number): Vector3 => [
    onN * N[0] + sign * axis[0],
    onN * N[1] + sign * axis[1],
    onN * N[2] + sign * axis[2],
  ];
  return [N, side(-s0, U, 1), side(s1, U, -1), side(-t0, V, 1), side(t1, V, -1)];
}

/** A ring cut by one side, keeping what's inside, its new edge along the side marked clipped. */
function clipRing(vertices: Vertex[], side: Vector3): Vertex[] {
  const kept: Vertex[] = [];
  for (let index = 0; index < vertices.length; index++) {
    const a = vertices[index] as Vertex;
    const b = vertices[(index + 1) % vertices.length] as Vertex;
    const da = dot(side, a.p);
    const db = dot(side, b.p);
    if (da >= 0) kept.push(a);
    if (da >= 0 !== db >= 0) {
      const t = da / (da - db);
      const crossing = { p: lerp(a.p, b.p, t), index: -1, width: 0 };
      // Leaving, the edge on to where it comes back runs along the side; entering, it's a's edge.
      kept.push({ ...crossing, clipped: da >= 0 ? true : a.clipped });
    }
  }
  return kept;
}

/** A line cut to every side at once: the runs of it inside, each ending where it was cut. */
function clipLine(vertices: Vertex[], sides: Vector3[]): Vertex[][] {
  const runs: Vertex[][] = [];
  let run: Vertex[] | null = null;
  const at = (a: Vertex, b: Vertex, t: number): Vertex => ({
    p: lerp(a.p, b.p, t),
    index: -1,
    width: a.width + (b.width - a.width) * t,
    clipped: false,
  });
  for (let index = 0; index + 1 < vertices.length; index++) {
    const a = vertices[index] as Vertex;
    const b = vertices[index + 1] as Vertex;
    let enter = 0;
    let leave = 1;
    for (const side of sides) {
      const da = dot(side, a.p);
      const db = dot(side, b.p);
      if (da < 0 && db < 0) {
        enter = 1;
        leave = 0;
        break;
      }
      if (da < 0) enter = Math.max(enter, da / (da - db));
      else if (db < 0) leave = Math.min(leave, da / (da - db));
    }
    if (enter > leave) {
      if (run) runs.push(run);
      run = null;
      continue;
    }
    if (!run || enter > 0) {
      if (run) runs.push(run);
      run = [enter > 0 ? at(a, b, enter) : a];
    }
    run.push(leave < 1 ? at(a, b, leave) : b);
    if (leave < 1) {
      runs.push(run);
      run = null;
    }
  }
  if (run) runs.push(run);
  return runs.filter(each => each.length >= 2);
}

/** Every chain cut to a place: lines to their footprint, rings to the place and its margin. */
function clipChains(
  surface: Surface,
  chains: Chain[],
  place: TilePlace,
  widestOf: (feature: number) => number,
): Chain[] {
  const margin = MARGIN_PIXELS / BASEMAP_TILE_PIXELS / tilesAcross(place.level);
  const ringSides = sidesOf(surface, place, margin);
  const cut: Chain[] = [];
  for (const chain of chains) {
    if (chain.closed) {
      let vertices = chain.vertices;
      for (const side of ringSides) {
        vertices = clipRing(vertices, side);
        if (vertices.length < 3) break;
      }
      if (vertices.length >= 3) cut.push({ ...chain, vertices });
      continue;
    }
    const sides = sidesOf(surface, place, margin + surface.reach(widestOf(chain.feature)));
    for (const vertices of clipLine(chain.vertices, sides)) cut.push({ ...chain, vertices });
  }
  return cut;
}

/**
 * Clips a feature set into its tile file's tiles, from level 9 down, and the detail map saying
 * where it goes deeper. Places with no feature in them have no tile.
 */
export async function featureSetTiles(
  features: readonly BasemapFeature[],
  rules: BasemapFeatureSetRules,
  geometry: BasemapGeometry,
): Promise<{ tiles: PmtilesTile[]; tree: LeafTree; detailMap: string }> {
  const surface = surfaceOf(geometry);
  const faces = geometry.kind === "sphere" ? 6 : 1;
  const tree = new LeafTree(BASEMAP_COARSEST_LEVEL, faces);
  const chains = chainsOf(features, surface);
  const widest = features.map(feature =>
    feature.geometry.type === "line"
      ? Math.max(0, ...feature.geometry.lines.flat().map(vertex => vertex[3]))
      : 0,
  );
  const importance = rules.importance;
  const rank = features.map(feature => {
    const value = importance ? feature.properties[importance] : 0;
    return typeof value === "number" ? value : 0;
  });

  const tiles: PmtilesTile[] = [];
  const visit = async (place: TilePlace, from: Chain[]) => {
    const exact = clipChains(surface, from, place, feature => widest[feature] ?? 0);
    if (exact.length === 0) return;
    const vertices = exact.reduce((sum, chain) => sum + chain.vertices.length, 0);
    const deeper = vertices > FEATURE_TILE_MOST_VERTICES && place.level < FEATURE_DEEPEST_LEVEL;
    const shown = deeper ? simplified(exact, place, rank) : exact;
    tiles.push({
      ...pmtilesAddress(place, geometry.kind),
      bytes: await encodeTile(shown, place, surface, features, rules),
    });
    if (!deeper) return;
    tree.split(place);
    for (const child of childrenOf(place)) await visit(child, exact);
  };
  for (let face = 0; face < faces; face++) {
    await visit({ face, level: BASEMAP_COARSEST_LEVEL, x: 0, y: 0 }, chains);
  }
  return { tiles, tree, detailMap: encodeDetailMap(tree) };
}

/** A feature set's whole tile file: its tiles, and metadata naming its detail map. */
export async function writeFeatureSetFile(
  name: string,
  features: readonly BasemapFeature[],
  rules: BasemapFeatureSetRules,
  geometry: BasemapGeometry,
): Promise<{ file: Uint8Array; detailMap: string; deepest: number }> {
  const { tiles, tree, detailMap } = await featureSetTiles(features, rules, geometry);
  const metadata = { format: BASEMAP_FORMAT, featureSet: name, type: "features", detailMap };
  return {
    file: await writePmtiles(tiles, "features", metadata),
    detailMap,
    deepest: tree.deepest,
  };
}

/** Each feature's lines and rings as chains of points that tile sides can cut. */
function chainsOf(features: readonly BasemapFeature[], surface: Surface): Chain[] {
  const pointOf = (face: number, x: number, y: number): Vector3 => {
    const u = surface.across(x / FACE_UNITS);
    const v = surface.across(1 - y / FACE_UNITS);
    return faceUvToDirection(face, u, v);
  };
  const chains: Chain[] = [];
  features.forEach((feature, index) => {
    if (feature.geometry.type === "line") {
      feature.geometry.lines.forEach((line, part) => {
        chains.push({
          feature: index,
          part,
          closed: false,
          vertices: line.map(([face, x, y, width], at) => ({
            p: pointOf(face, x, y),
            index: at,
            width,
            clipped: false,
          })),
        });
      });
      return;
    }
    feature.geometry.polygons.forEach((polygon, part) => {
      polygon.forEach((ring, ringIndex) => {
        chains.push({
          feature: index,
          part,
          ring: ringIndex,
          closed: true,
          vertices: ring.map(([face, x, y], at) => ({
            p: pointOf(face, x, y),
            index: at,
            width: 0,
            clipped: false,
          })),
        });
      });
    });
  });
  return chains;
}

/** A chain's vertices as positions across a tile's face. */
function facePositions(chain: Chain, face: number, surface: Surface): number[] {
  const positions: number[] = [];
  for (const { p } of chain.vertices) {
    const [u, v] = directionToFaceUv(face, p);
    positions.push(toUnits(surface.back(u)), toUnits(1 - surface.back(v)));
  }
  return positions;
}

const toUnits = (share: number) =>
  Math.min(FACE_UNITS - 1, Math.max(0, Math.round(share * FACE_UNITS)));

/**
 * A coarser tile's chains, simplified to its pixel size: each line and ring kept within a pixel of
 * itself, never moving a vertex clipping made, and polygons and lines under a pixel dropped. Past
 * the tile's vertex budget, features go in order of importance.
 */
function simplified(chains: Chain[], place: TilePlace, rank: number[]): Chain[] {
  const pixel = 1 / (BASEMAP_TILE_PIXELS * tilesAcross(place.level));
  const { N, U, V } = faceAxes(place.face);
  // A rough position across the face, enough to measure in pixels.
  const flat = (p: Vector3): [number, number] => {
    const n = dot(p, N);
    return [dot(p, U) / n / 2, dot(p, V) / n / 2];
  };
  const kept: Chain[] = [];
  const dropped = new Set<string>();
  for (const chain of chains) {
    const points = chain.vertices.map(vertex => flat(vertex.p));
    if (chain.closed) {
      if (Math.abs(ringArea(points)) < pixel * pixel) {
        // An outer ring too small to see takes its polygon's holes with it.
        if (chain.ring === 0) dropped.add(`${chain.feature}/${chain.part}`);
        continue;
      }
    } else if (lengthOf(points) < pixel) continue;
    kept.push({ ...chain, vertices: simplifyChain(chain, points, pixel) });
  }
  const shown = kept.filter(chain => !dropped.has(`${chain.feature}/${chain.part}`));
  let total = shown.reduce((sum, chain) => sum + chain.vertices.length, 0);
  if (total <= FEATURE_TILE_MOST_VERTICES) return shown;
  const order = [...new Set(shown.map(chain => chain.feature))].sort(
    (a, b) => (rank[b] ?? 0) - (rank[a] ?? 0),
  );
  const budget = new Set<number>();
  total = 0;
  for (const feature of order) {
    const size = shown
      .filter(chain => chain.feature === feature)
      .reduce((sum, chain) => sum + chain.vertices.length, 0);
    if (total + size > FEATURE_TILE_MOST_VERTICES) continue;
    budget.add(feature);
    total += size;
  }
  return shown.filter(chain => budget.has(chain.feature));
}

/**
 * Douglas–Peucker within `tolerance`, run separately between each pair of vertices clipping made,
 * so clipped edges and ends stay exactly where they were.
 */
function simplifyChain(chain: Chain, points: [number, number][], tolerance: number): Vertex[] {
  const { vertices } = chain;
  const keep = new Uint8Array(vertices.length);
  const anchors: number[] = [];
  vertices.forEach((vertex, index) => {
    if (vertex.index === -1) anchors.push(index);
  });
  if (!chain.closed) anchors.push(0, vertices.length - 1);
  if (chain.closed && anchors.length === 0) {
    // A whole ring is anchored at its first vertex and the one farthest from it.
    let far = 0;
    let farthest = -1;
    const [x0, y0] = points[0] as [number, number];
    points.forEach(([x, y], index) => {
      const distance = (x - x0) ** 2 + (y - y0) ** 2;
      if (distance > farthest) [far, farthest] = [index, distance];
    });
    anchors.push(0, far);
  }
  const sorted = [...new Set(anchors)].sort((a, b) => a - b);
  for (const anchor of sorted) keep[anchor] = 1;
  const spans: [number, number][] = [];
  for (let index = 0; index + 1 < sorted.length; index++) {
    spans.push([sorted[index] as number, sorted[index + 1] as number]);
  }
  if (chain.closed && sorted.length > 0) {
    spans.push([sorted.at(-1) as number, (sorted[0] as number) + vertices.length]);
  }
  const point = (index: number) => points[index % vertices.length] as [number, number];
  const reduce = (from: number, to: number) => {
    if (to - from < 2) return;
    const [ax, ay] = point(from);
    const [bx, by] = point(to);
    const length = Math.hypot(bx - ax, by - ay) || 1e-30;
    let worst = -1;
    let at = from;
    for (let index = from + 1; index < to; index++) {
      const [px, py] = point(index);
      const off = Math.abs((bx - ax) * (ay - py) - (ax - px) * (by - ay)) / length;
      if (off > worst) [worst, at] = [off, index];
    }
    if (worst <= tolerance) return;
    keep[at % vertices.length] = 1;
    reduce(from, at);
    reduce(at, to);
  };
  for (const [from, to] of spans) reduce(from, to);
  // An edge stays clipped only where both its ends are the ends of one clipped edge.
  const result: Vertex[] = [];
  vertices.forEach((vertex, index) => {
    if (!keep[index]) return;
    const next = (index + 1) % vertices.length;
    result.push({ ...vertex, clipped: vertex.clipped && keep[next] === 1 });
  });
  return result.length >= (chain.closed ? 3 : 2) ? result : vertices;
}

function ringArea(points: [number, number][]): number {
  let sum = 0;
  for (let index = 0; index < points.length; index++) {
    const [ax, ay] = points[index] as [number, number];
    const [bx, by] = points[(index + 1) % points.length] as [number, number];
    sum += ax * by - bx * ay;
  }
  return sum / 2;
}

function lengthOf(points: [number, number][]): number {
  let sum = 0;
  for (let index = 0; index + 1 < points.length; index++) {
    const [ax, ay] = points[index] as [number, number];
    const [bx, by] = points[index + 1] as [number, number];
    sum += Math.hypot(bx - ax, by - ay);
  }
  return sum;
}

/** A tile's chains, feature by feature, with the set's properties as columns, gzipped. */
async function encodeTile(
  chains: Chain[],
  place: TilePlace,
  surface: Surface,
  features: readonly BasemapFeature[],
  rules: BasemapFeatureSetRules,
): Promise<Uint8Array> {
  const byFeature = new Map<number, Chain[]>();
  for (const chain of chains) {
    const list = byFeature.get(chain.feature) ?? [];
    list.push(chain);
    byFeature.set(chain.feature, list);
  }
  const order = [...byFeature.keys()].sort((a, b) => a - b);
  const tile: BasemapFeatureTile = {
    features: order.map(index => ({
      id: (features[index] as BasemapFeature).id,
      pieces: (byFeature.get(index) ?? []).map(chain => {
        const piece: BasemapFeatureTile["features"][number]["pieces"][number] = {
          part: chain.part,
          positions: facePositions(chain, place.face, surface),
          vertices: chain.vertices.map(vertex => vertex.index),
        };
        if (chain.closed) {
          piece.ring = chain.ring ?? 0;
          const clipped = chain.vertices.flatMap((vertex, at) => (vertex.clipped ? [at] : []));
          if (clipped.length > 0) piece.clippedEdges = clipped;
        } else piece.widths = chain.vertices.map(vertex => vertex.width);
        return piece;
      }),
    })),
    properties: Object.fromEntries(
      rules.properties.map(({ name }) => [
        name,
        order.map(index => (features[index] as BasemapFeature).properties[name] ?? ""),
      ]),
    ),
  };
  return transform(new TextEncoder().encode(JSON.stringify(tile)), new CompressionStream("gzip"));
}
